/**
 * Checkout — the confirmation page between a pricing CTA and the money.
 *
 * Two shapes arrive here:
 *
 *  1. TIER checkout (`?tier=solo|pro&interval=…`) — the public pricing ladder
 *     and the in-app tier ladder. A tier is a FLAT bundle: it owns its price,
 *     its seat count and its module list. Everything rendered for it comes from
 *     that tier's own TIER_CARDS entry (served on /billing/pricing → `tiers`),
 *     never re-derived from base price × seats + modules. Re-deriving it is how
 *     a visitor who clicked the €49 Pro card used to land on "After trial:
 *     €0.00/mo".
 *
 *  2. LEGACY à-la-carte checkout (`?interval&seats&stakeholders&modules`) — the
 *     older base-plan + add-on-modules flow, still reached from the trial
 *     expiration modal. Kept working; its summary is the module breakdown.
 *
 * Trial vs paid is decided here, and only ever in the safe direction:
 *   - Default is the reverse trial (no card, no charge).
 *   - A org that already used its trial (`status` free / past_due, the same
 *     rule routes/billing.ts applies) is shown a PAID checkout up front, with
 *     the amount due today spelled out and a banner saying why it is asking
 *     for money — not a "start your free trial" button that dead-ends on a 400.
 *   - If the backend rejects the trial anyway (TRIAL_ALREADY_USED), the page
 *     switches to paid mode and waits for a second, explicit click. It never
 *     auto-retries as a charge.
 *   - No price is ever rendered from a catalogue that has not loaded. Both
 *     shapes wait, and a failed load is a retryable error, never a €0.00 total
 *     over a live buy button.
 */
import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button, Spinner, Banner } from '../components/serac';
import MarketingHeader from '../components/common/MarketingHeader';
import MarketingFooter from '../components/common/MarketingFooter';
import { MODULE_ICONS, type ModuleInfo } from '../components/billing/ModuleSelector';
import { apiClient } from '../api/client';
import { portalApi, type TierCardInfo } from '../api/portal';
import { useAuth } from '../contexts/AuthContext';

type BillingInterval = 'monthly' | 'yearly';

const STAKEHOLDER_PRICE_MONTHLY = 5;
const STAKEHOLDER_PRICE_YEARLY = 55;

/**
 * Tiers that can be bought without talking to sales.
 *
 * Mirrors the backend allow-list in routes/billing.ts
 * (`['solo','pro'].includes(tier)`); Team and Enterprise are priced -1 in
 * TIER_PRESETS and 400 there. Validating the URL parameter against it is the
 * point: an unvalidated cast lets `?tier=enterprise` reach the API and the
 * visitor sees a raw backend error instead of a sales route.
 *
 * This is a list of IDs, not pricing data — every price, label, seat line and
 * feature bullet still comes from the TIER_CARDS payload.
 */
const SELF_SERVE_TIERS = ['solo', 'pro'] as const;
type SelfServeTier = (typeof SELF_SERVE_TIERS)[number];

const isSelfServeTier = (value: string | null): value is SelfServeTier =>
  value !== null && (SELF_SERVE_TIERS as readonly string[]).includes(value);

/**
 * Subscription statuses that mean "this org has already had its trial".
 * Same set routes/billing.ts refuses `withTrial` for, so the page's up-front
 * copy matches what the API will actually do. An org that never subscribed has
 * no subscription row at all and is offered the trial.
 */
const TRIAL_SPENT_STATUSES = ['free', 'past_due'];

/**
 * Did the API refuse this checkout because the trial is spent?
 *
 * `code` is the signal, and both shapes carry it: the tier branch and the
 * legacy à-la-carte branch both refuse through `decideCheckoutEligibility`
 * (routes/billing.ts), which answers `code: 'TRIAL_ALREADY_USED'`.
 *
 * The message match behind it is belt-and-braces for a response that loses the
 * code (a proxy rewriting the body, an older backend). It is safe to keep
 * because it can only ever turn a raw error into the paid summary — never into
 * a charge, which still needs a second, explicit click.
 */
export function isTrialAlreadyUsed(err: unknown): boolean {
  const data = (err as { response?: { data?: { code?: string; error?: string } } })?.response?.data;
  if (!data) return false;
  if (data.code === 'TRIAL_ALREADY_USED') return true;
  return typeof data.error === 'string' && /trial already used/i.test(data.error);
}

export default function Checkout() {
  const { t } = useTranslation(['billing', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPortalAuthenticated, portalSession } = useAuth();

  const billingInterval: BillingInterval = searchParams.get('interval') === 'yearly' ? 'yearly' : 'monthly';
  const developerSeats = Math.min(10, Math.max(1, parseInt(searchParams.get('seats') || '1') || 1));
  const stakeholderSeats = parseInt(searchParams.get('stakeholders') || '0') || 0;
  const selectedModuleIds = searchParams.get('modules')?.split(',').filter(Boolean) || [];

  // A tier was asked for; whether it is one we can actually sell is the next line.
  const tierParam = searchParams.get('tier');
  const tierRequested = tierParam !== null && tierParam !== '';
  const tierId: SelfServeTier | null = isSelfServeTier(tierParam) ? tierParam : null;

  const [error, setError] = useState<string | null>(null);
  // Set only after the API refuses the trial. Flips the page to paid mode and
  // explains why — it never fires the paid checkout by itself.
  const [trialRefused, setTrialRefused] = useState(false);

  const plan = developerSeats >= 2 ? 'team' : 'individual';
  const intervalLabel = billingInterval === 'monthly' ? 'mo' : 'yr';

  // Canonical pricing: module catalogue for the legacy summary, TIER_CARDS for
  // the tier summary. One fetch, same source the marketing manifest is built from.
  const { data: pricingData, isPending: pricingPending, isError: pricingFailed } = useQuery({
    queryKey: ['pricing-modules'],
    queryFn: () => portalApi.getPricing(),
  });

  const modules: ModuleInfo[] = pricingData?.modules || [];
  const tierCard: TierCardInfo | null =
    (tierId && pricingData?.tiers?.find((card) => card.id === tierId)) || null;

  /**
   * Signed out — send them to the auth screen and carry the whole checkout URL
   * along, tier included.
   *
   * The key MUST be `returnUrl`. That is the only one anything reads:
   * Home.tsx's `getRedirectDestination()` is what every post-auth path calls,
   * and it falls back to /portal/dashboard when `returnUrl` is absent;
   * IndexPage's `hasAuthParam` list is `tab|mode|token|trial_token|returnUrl`.
   * This line used to say `redirect=`, a key that appears nowhere else in the
   * app — so a signed-out visitor clicking the €49 Pro card registered and
   * landed on the dashboard with the tier thrown away, which is the whole
   * defect this page was fixed for, on the audience the public pricing page
   * actually has.
   *
   * Home decodeURIComponent()s the value, and useSearchParams already decoded
   * it once, so the plain `/checkout?tier=pro&interval=monthly` round-trips.
   *
   * Still lost on one leg: a brand-new account finishes on a `/?token=…` link
   * from the verification email, which carries no query state of its own. That
   * needs the verification link to carry the destination — backend work,
   * outside this page.
   */
  useEffect(() => {
    if (!isPortalAuthenticated) {
      const returnUrl = `/checkout?${searchParams.toString()}`;
      navigate(`/?mode=register&returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  }, [isPortalAuthenticated, navigate, searchParams]);

  /**
   * Nothing was chosen. A bare /checkout carries no tier, no modules and no
   * seat count — the only caller left is PaymentComplete's "try again" after a
   * failed payment, and the page used to answer it by confirming the €19 base
   * plan. Confirming a plan the visitor never picked is how someone ends up
   * paying for something they did not choose, so send them to the ladder to
   * pick one. Any URL that DOES name a tier or modules is untouched.
   */
  const nothingChosen =
    !tierRequested && selectedModuleIds.length === 0 && searchParams.get('seats') === null;
  useEffect(() => {
    if (isPortalAuthenticated && nothingChosen) navigate('/pricing', { replace: true });
  }, [isPortalAuthenticated, nothingChosen, navigate]);

  // A live subscription is a plan CHANGE, not a checkout — the billing page owns that.
  useEffect(() => {
    if (portalSession?.subscription && ['active', 'trialing'].includes(portalSession.subscription.status)) {
      navigate('/portal/billing');
    }
  }, [portalSession, navigate]);

  const subscriptionStatus = portalSession?.subscription?.status;
  const trialAvailable = !trialRefused && !TRIAL_SPENT_STATUSES.includes(subscriptionStatus || '');
  /**
   * Why this is a paid page. `free` is where the trial-expiry worker lands an
   * expired trial, so that org's trial is provably spent — say so.
   */
  const explainTrialSpent = trialRefused || subscriptionStatus === 'free';
  /**
   * `past_due` is a different story and gets its own sentence. Telling that
   * customer they "used their trial" would be a guess — they may have gone
   * straight to paid — but a page that silently asks for money is worse, so it
   * names the failed payment and says the new plan replaces the old one.
   *
   * That claim is true: handleFirstPaymentSuccess cancels the superseded
   * subscription and the Mollie subscription behind it before creating the new
   * one (cancelSupersededMollieSubscription), so this does not stack a second
   * charge on top of the broken one.
   *
   * Deliberately NOT bounced to /portal/billing the way active/trialing are.
   * The recurring charge failed, so there is nothing to re-price — the repair
   * IS a fresh first payment, which is what collects a new mandate, and the
   * backend permits it (decideCheckoutEligibility refuses 'active' only).
   * TierLadder reaches the same conclusion from the other side: its 'past-due'
   * viewer gets a live "Restore payment for {tier}" checkout rather than the
   * support route. The alternative — one dunning surface, past_due redirected
   * to billing — would mean this page silently dropping a customer who came
   * from the public pricing ladder, and it only works while the billing page
   * keeps that restore action.
   */
  const explainPaymentFailed = subscriptionStatus === 'past_due' && !trialRefused;

  const checkoutMutation = useMutation({
    mutationFn: (withTrial: boolean) =>
      apiClient.portalCheckout(
        tierId
          ? {
              // A flat tier carries neither seats nor modules: TIER_PRESETS
              // owns includedSeats (Pro = 3), and passing selectedModules
              // alongside a tier is rejected outright by the API.
              tier: tierId,
              interval: billingInterval,
              withTrial,
            }
          : {
              plan: plan as 'individual' | 'team',
              interval: billingInterval,
              developerSeats,
              stakeholderSeats: plan === 'team' ? stakeholderSeats : 0,
              selectedModules: selectedModuleIds,
              withTrial,
            },
      ),
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      // No-card reverse-trial started server-side — go straight to the
      // post-trial page (no Mollie round-trip).
      window.location.href = '/billing/payment-complete';
    },
    onError: (err: unknown) => {
      // The trial is spent. Show the paid plan honestly and wait for a second
      // click; silently retrying as a charge would bill someone who pressed a
      // button labelled "free".
      if (isTrialAlreadyUsed(err)) {
        setTrialRefused(true);
        setError(null);
        return;
      }
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || t('billing:checkout.checkoutFailed'));
    },
  });

  const { baseCost, modulesCost, stakeholderCost, totalCost } = useMemo(() => {
    const base = modules.find(m => m.isBase);
    const basePrice = base ? (billingInterval === 'monthly' ? base.monthlyPrice : base.yearlyPrice) : 0;
    const baseCost = basePrice * developerSeats;

    let modCost = 0;
    for (const modId of selectedModuleIds) {
      const mod = modules.find(m => m.id === modId && !m.isBase);
      if (mod) {
        const price = billingInterval === 'monthly' ? mod.monthlyPrice : mod.yearlyPrice;
        modCost += price * (mod.isPerSeat ? developerSeats : 1);
      }
    }

    const extraStakeholders = Math.max(0, stakeholderSeats - 1);
    const shPrice = billingInterval === 'monthly' ? STAKEHOLDER_PRICE_MONTHLY : STAKEHOLDER_PRICE_YEARLY;
    const stakeholderCost = extraStakeholders * shPrice;

    return { baseCost, modulesCost: modCost, stakeholderCost, totalCost: baseCost + modCost + stakeholderCost };
  }, [modules, selectedModuleIds, billingInterval, developerSeats, stakeholderSeats]);

  // Resolve selected module objects
  const selectedAddOnModules = modules.filter(m => selectedModuleIds.includes(m.id) && !m.isBase);
  const baseModule = modules.find(m => m.isBase);

  // The money strings. For a tier they are the card's OWN labels — TierCard
  // carries text, not cents, and re-deriving a number from the module
  // catalogue is exactly the bug this page had.
  const priceLabel = tierCard
    ? billingInterval === 'monthly' ? tierCard.monthlyLabel : tierCard.yearlyLabel
    : `€${totalCost.toFixed(2)}`;
  const priceUnit = tierCard
    ? billingInterval === 'monthly' ? tierCard.monthlyUnit : tierCard.yearlyUnit
    : `/${intervalLabel}`;

  if (!isPortalAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const shell = (children: ReactNode) => (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MarketingHeader rightContent={
        <Link to="/pricing"><Button variant="quiet" size="sm">{t('billing:checkout.backToPricing')}</Button></Link>
      } />
      <div className="max-w-3xl mx-auto px-4 py-12 sm:px-6 lg:px-8 flex-1 w-full">
        {children}
      </div>
      <MarketingFooter />
    </div>
  );

  // No catalogue, no prices — for EITHER shape. The tier branch needs the card's
  // own labels (falling through to the à-la-carte summary is the €0.00 bug); the
  // à-la-carte branch needs the module list it sums, and without it `totalCost`
  // is 0, which on a paid checkout renders "Due today €0.00" over a live button
  // that charges the real €19+ at Mollie. Gate both on the payload itself.
  if (!pricingData && pricingPending) {
    return shell(
      <div className="flex justify-center py-24"><Spinner size="lg" /></div>
    );
  }

  // A catalogue that failed to load is a retryable outage, not a verdict on the
  // plan. Saying "this plan isn't self-serve" here would send a paying customer
  // to sales over a network blip.
  if (!pricingData && pricingFailed) {
    return shell(
      <Card>
        <Banner tone="fail">{t('billing:checkout.checkoutFailed')}</Banner>
        <div className="mt-6">
          <Link to="/pricing"><Button variant="ice">{t('billing:checkout.viewPlans')}</Button></Link>
        </div>
      </Card>,
    );
  }

  // Asked for a plan we cannot sell self-serve: `?tier=enterprise` and typos are
  // caught by the allow-list without any data, and a tier the loaded catalogue
  // does not carry lands here too. Route it to sales rather than letting the API
  // answer with a 400 the visitor cannot act on.
  if (tierRequested && !tierCard) {
    return shell(
      <Card>
        <h1 className="text-2xl font-bold text-gray-900">{t('billing:checkout.tierUnavailableTitle')}</h1>
        <p className="mt-3 text-gray-600">{t('billing:checkout.tierUnavailableBody')}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/pricing"><Button variant="ice">{t('billing:checkout.viewPlans')}</Button></Link>
          <a href="mailto:sales@serac.build?subject=Serac%20Team%20%26%20Enterprise">
            <Button variant="quiet">{t('billing:checkout.talkToSales')}</Button>
          </a>
        </div>
      </Card>,
    );
  }

  return shell(
    <>
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('billing:checkout.title')}</h1>
        <p className="mt-2 text-gray-600">
          {trialAvailable ? t('billing:checkout.subtitle') : t('billing:checkout.subtitlePaid')}
        </p>
      </div>

      {/* Already included for free */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 text-sm">{t('billing:checkout.alreadyIncluded')}</h3>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <span>&#10003; {t('billing:freePlan.features.tools')}</span>
              <span>&#10003; {t('billing:freePlan.features.mcp')}</span>
              <span>&#10003; {t('billing:freePlan.features.byollm')}</span>
              <span>&#10003; {t('billing:freePlan.features.instanceChat')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Plan details */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('billing:checkout.yourPlan')}</h2>
          <Link to={`/pricing`} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            {t('billing:checkout.editPlan')}
          </Link>
        </div>

        {tierCard ? (
          /* The clicked card, rendered from its own strings. */
          <div>
            <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
              <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{tierCard.name}</span>
              {tierCard.badge && (
                <span className="text-[11px] font-medium uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                  {tierCard.badge}
                </span>
              )}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">{priceLabel}</span>
              <span className="text-sm text-gray-500">{priceUnit}</span>
            </div>
            <p className="mt-1 text-sm font-medium text-blue-600">{tierCard.seatCopy}</p>
            <p className="mt-2 text-sm text-gray-600">{tierCard.tagline}</p>

            <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {t('billing:checkout.whatsIncluded')}
            </h3>
            <ul className="space-y-2">
              {tierCard.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-blue-600 mt-0.5">&#10003;</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wider">{t('billing:plan.type')}</span>
                <p className="font-semibold text-gray-900 capitalize mt-0.5">{plan === 'team' ? t('billing:plan.team') : t('billing:plan.individual')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wider">{t('billing:summary.billing')}</span>
                <p className="font-semibold text-gray-900 capitalize mt-0.5">
                  {billingInterval === 'monthly' ? t('billing:plan.monthly') : t('billing:plan.yearly')}
                  {billingInterval === 'yearly' && <span className="text-green-600 text-xs font-normal ml-1">{t('billing:plan.yearlyFreeMonth')}</span>}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wider">{t('billing:summary.developerSeats')}</span>
                <p className="font-semibold text-gray-900 mt-0.5">{developerSeats}</p>
              </div>
              {plan === 'team' && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{t('billing:summary.stakeholderSeats')}</span>
                  <p className="font-semibold text-gray-900 mt-0.5">
                    {stakeholderSeats} <span className="text-green-600 text-xs font-normal">{t('billing:summary.stakeholderFree', { paid: stakeholderSeats > 1 ? t('billing:summary.stakeholderPaid', { count: stakeholderSeats - 1 }) : '' })}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Modules */}
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('billing:sections.modules')}</h3>
            <div className="space-y-2">
              {baseModule && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    {MODULE_ICONS[baseModule.iconName] || MODULE_ICONS.terminal}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900 text-sm">{baseModule.name}</span>
                    <span className="ml-2 text-xs text-blue-600 font-medium">{t('billing:modules.base')}</span>
                  </div>
                  <span className="text-sm text-gray-600">
                    &euro;{(billingInterval === 'monthly' ? baseModule.monthlyPrice : baseModule.yearlyPrice).toFixed(2)}/{intervalLabel}{t('billing:modules.perSeat')}
                  </span>
                </div>
              )}
              {selectedAddOnModules.map(mod => {
                const price = billingInterval === 'monthly' ? mod.monthlyPrice : mod.yearlyPrice;
                return (
                  <div key={mod.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                      {MODULE_ICONS[mod.iconName] || MODULE_ICONS.terminal}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900 text-sm">{mod.name}</span>
                      {mod.toolCount > 0 && (
                        <span className="ml-2 text-xs text-gray-400">{t('billing:modules.tools', { count: mod.toolCount })}</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-600">
                      &euro;{price.toFixed(2)}/{intervalLabel}{mod.isPerSeat && t('billing:modules.perSeat')}
                    </span>
                  </div>
                );
              })}
              {selectedAddOnModules.length === 0 && (
                <p className="text-sm text-gray-500 py-2">{t('billing:summary.noAddOns')}</p>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Cost breakdown + Payment */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('billing:summary.paymentSummary')}</h2>

        {/* Line-item breakdown belongs to the à-la-carte flow only. A flat tier
            has one price; itemising it would mean inventing numbers the tier
            card does not carry. */}
        {!tierCard && (
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('billing:summary.basePlan', { count: developerSeats, seatLabel: developerSeats === 1 ? t('billing:summary.seat') : t('billing:summary.seats') })}</span>
              <span>&euro;{baseCost.toFixed(2)}/{intervalLabel}</span>
            </div>
            {stakeholderCost > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('billing:summary.extraStakeholders', { count: stakeholderSeats - 1 })}</span>
                <span>&euro;{stakeholderCost.toFixed(2)}/{intervalLabel}</span>
              </div>
            )}
            {modulesCost > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('billing:summary.addOnModules', { count: selectedAddOnModules.length })}</span>
                <span>&euro;{modulesCost.toFixed(2)}/{intervalLabel}</span>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-200 pt-3 mb-6">
          {trialAvailable ? (
            <>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{t('billing:trial.thirtyDayFree')}</span>
                <span className="text-green-600 font-medium">{t('billing:trial.free')}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-gray-600">{t('billing:trial.afterTrial')}</span>
                <span className="text-2xl font-bold text-gray-900">
                  {priceLabel}<span className="text-sm font-normal text-gray-500 ml-1">{priceUnit}</span>
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-baseline">
                <span className="text-gray-600">{t('billing:checkout.dueToday')}</span>
                <span className="text-2xl font-bold text-gray-900">{priceLabel}</span>
              </div>
              <p className="mt-1 text-sm text-gray-500 text-right">
                {t('billing:checkout.thenRecurring', { price: priceLabel, unit: priceUnit })}
              </p>
            </>
          )}
        </div>

        {/* Why we are asking for money. Rendered AFTER the total on purpose:
            both strings end with "the amount due today is shown above", and on
            the à-la-carte branch the only figure above the old position was the
            line-item breakdown, not the total. */}
        {explainTrialSpent && (
          <Banner tone="warn" style={{ marginBottom: 16 }}>{t('billing:checkout.trialAlreadyUsed')}</Banner>
        )}
        {explainPaymentFailed && (
          <Banner tone="warn" style={{ marginBottom: 16 }}>{t('billing:checkout.paymentFailed')}</Banner>
        )}

        <Banner tone="note" title={t('billing:howItWorks.title')} style={{ marginBottom: 16 }}>
          <ul className="space-y-1">
            {trialAvailable ? (
              <>
                <li>{t('billing:howItWorks.step1Charge')}</li>
                <li>{t('billing:howItWorks.step2')}</li>
                <li>{t('billing:howItWorks.step3')}</li>
                <li>{t('billing:howItWorks.step4')}</li>
              </>
            ) : (
              <>
                <li>{t('billing:checkout.paidStep1')}</li>
                <li>{t('billing:checkout.paidStep2')}</li>
                <li>{t('billing:checkout.paidStep3')}</li>
              </>
            )}
          </ul>
        </Banner>

        {error && (
          <Banner tone="fail" style={{ marginBottom: 16 }}>{error}</Banner>
        )}

        <Button
          variant="ice"
          className="w-full py-3 text-base"
          onClick={() => { setError(null); checkoutMutation.mutate(trialAvailable); }}
          disabled={checkoutMutation.isPending}
        >
          {checkoutMutation.isPending
            ? trialAvailable ? t('billing:checkout.startingTrial') : t('billing:checkout.startingPayment')
            : trialAvailable
              ? t('billing:checkout.startFreeTrial')
              : tierCard
                ? t('billing:checkout.subscribeTo', { tier: tierCard.name })
                : t('billing:checkout.subscribeNow')}
        </Button>

        <p className="text-xs text-gray-500 text-center mt-4">
          {trialAvailable ? t('billing:checkout.termsPrefix') : t('billing:checkout.termsPrefixPaid')}{' '}
          <a href="/legal/terms" className="text-blue-600 hover:underline">{t('billing:checkout.termsLink')}</a>
          {' '}{t('billing:checkout.termsAnd')}{' '}
          <a href="/legal/privacy" className="text-blue-600 hover:underline">{t('billing:checkout.privacyLink')}</a>.
        </p>
      </Card>

      {/* A trial takes no payment — reassure instead of showing payment-method
          logos (which read as "we need your card"). A paid checkout DOES take
          one, so it says so rather than repeating the trial promise. */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          {trialAvailable
            ? `🔒 ${t('billing:checkout.noCardRequired')}`
            : `🔒 ${t('billing:checkout.securePayment')}`}
        </p>
      </div>
    </>,
  );
}
